/**
 * First-party ad attribution capture.
 *
 * The edge proxy inspects every page navigation for campaign parameters
 * (utm_*) and ad-network click identifiers (gclid, fbclid, twclid, rdt_cid,
 * ...) and folds them into the `sq_attr` cookie: the FIRST touch is kept for
 * the cookie's lifetime, the LAST campaign touch is overwritten on every new
 * ad arrival. At signup the cookie is persisted to `user_attribution`, tying
 * the acquisition channel to the account.
 *
 * The click identifiers are the load-bearing part for the roadmap: the
 * server-side conversion callbacks (Google offline conversions, Meta CAPI,
 * X/Reddit conversion APIs) all key on the network's click ID, so it must be
 * captured at landing time — it cannot be reconstructed later.
 *
 * Everything here is pure and dependency-free so it runs in the edge runtime
 * (proxy.ts) and in node route handlers alike.
 *
 * CAMPAIGN TAGGING CONVENTION (configure in each ads manager): tag every ad
 * URL with `utm_source=<network slug>` using the CLICK_ID_NETWORKS slugs —
 * `x`, `google`, `facebook`, `reddit` (and `bing`/`tiktok`/`linkedin` if we
 * ever advertise there) — plus a `utm_campaign` matching the campaign's name
 * on the platform. That keeps explicit tags and click-ID-derived fallbacks in
 * the same vocabulary, so admin rollups never split one channel in two.
 */

export const ATTRIBUTION_COOKIE = "sq_attr";

/** 90 days covers every ad network's click-attribution window (Google's is the longest at 90). */
export const ATTRIBUTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

/**
 * Ad-network click identifier → network slug. The slug doubles as the derived
 * acquisition source when no utm_source was set on the landing URL. Networks
 * we don't advertise on yet are included where the param is standardized —
 * capturing them is free and retrofitting is impossible.
 */
export const CLICK_ID_NETWORKS: Record<string, string> = {
  gclid: "google",
  gbraid: "google",
  wbraid: "google",
  fbclid: "facebook",
  twclid: "x",
  rdt_cid: "reddit",
  msclkid: "bing",
  ttclid: "tiktok",
  li_fat_id: "linkedin",
};

const CAPTURED_KEYS: readonly string[] = [...UTM_KEYS, ...Object.keys(CLICK_ID_NETWORKS)];

// Caps keep the cookie well under the 4KB header budget even with every
// captured key present on both touches (values are attacker-controlled URL
// params — a hostile 2KB utm_campaign must not break the site's requests).
const MAX_VALUE_CHARS = 100;
const MAX_PATH_CHARS = 150;
const MAX_REF_CHARS = 200;
const MAX_COOKIE_CHARS = 3500;

export interface AttributionTouch {
  /** Epoch milliseconds when the touch landed. */
  at: number;
  /** Landing pathname (no query — the interesting params are in `params`). */
  path: string;
  /** External referrer (origin + path), absent for direct / same-site navigation. */
  ref?: string;
  /** Captured allowlisted params (utm_* + click IDs), values length-capped. */
  params: Record<string, string>;
}

export interface AttributionState {
  first: AttributionTouch;
  last: AttributionTouch;
}

const cap = (value: string, max: number): string => (value.length > max ? value.slice(0, max) : value);

/** Hostname of a `Host` header value (port stripped), or null when unusable. */
const hostnameOf = (hostHeader: string | null): string | null => {
  if (!hostHeader) return null;
  try {
    return new URL(`http://${hostHeader}`).hostname;
  } catch {
    return null;
  }
};

const externalReferrer = (referrer: string | null, siteHosts: readonly string[]): string | undefined => {
  if (!referrer) return undefined;
  try {
    const url = new URL(referrer);
    // Non-http(s) referrers (android-app:// from email clients, etc.) have an
    // OPAQUE origin that serializes to the literal string "null" — storing
    // origin+path for one produced a "null/" referrer in prod. They carry no
    // web acquisition signal, so they're treated as direct.
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    if (siteHosts.includes(url.hostname)) return undefined;
    return cap(`${url.origin}${url.pathname}`, MAX_REF_CHARS);
  } catch {
    return undefined;
  }
};

/**
 * Extract an attribution touch from a landing request, or null when the
 * request carries nothing attributable (no campaign params and no external
 * referrer — i.e. direct traffic or internal navigation).
 *
 * `hostHeader` is the request's `Host` header. Behind nginx the app is reached
 * at an internal address, so `url.hostname` is NOT the public host — comparing
 * only against it made every internal navigation look like an external
 * referral from our own site. nginx forwards the public host as `Host`, so both
 * count as "this site". Prod: 54 of the first 180 first-touch rows (2026-07-13
 * → 09-10) recorded squidler.io as their referrer, which the admin rolled up
 * as an acquisition source.
 */
export const captureTouch = (
  url: { hostname: string; pathname: string; searchParams: URLSearchParams },
  referrer: string | null,
  now: number,
  hostHeader: string | null = null,
): AttributionTouch | null => {
  const params = Object.fromEntries(
    CAPTURED_KEYS.map((key): [string, string] => [key, (url.searchParams.get(key) ?? "").trim()])
      .filter(([, value]) => value !== "")
      .map(([key, value]) => [key, cap(value, MAX_VALUE_CHARS)]),
  );
  const forwardedHost = hostnameOf(hostHeader);
  const siteHosts = forwardedHost ? [url.hostname, forwardedHost] : [url.hostname];
  const ref = externalReferrer(referrer, siteHosts);
  if (Object.keys(params).length === 0 && ref === undefined) return null;
  return {
    at: now,
    path: cap(url.pathname, MAX_PATH_CHARS),
    ...(ref !== undefined ? { ref } : {}),
    params,
  };
};

/**
 * Fold a new touch into the existing cookie state. Returns the new state, or
 * null when nothing should change (so the caller skips the Set-Cookie):
 * first-touch is immutable for the cookie's lifetime, and a referrer-only
 * visit never displaces a recorded campaign last-touch.
 */
export const mergeAttribution = (
  existing: AttributionState | null,
  touch: AttributionTouch,
): AttributionState | null => {
  if (!existing) return { first: touch, last: touch };
  if (Object.keys(touch.params).length === 0) return null;
  return { first: existing.first, last: touch };
};

const isTouch = (value: unknown): value is AttributionTouch => {
  if (typeof value !== "object" || value === null) return false;
  const t = value as Partial<AttributionTouch>;
  return (
    typeof t.at === "number" &&
    typeof t.path === "string" &&
    (t.ref === undefined || typeof t.ref === "string") &&
    typeof t.params === "object" &&
    t.params !== null &&
    Object.values(t.params).every((v) => typeof v === "string")
  );
};

/*
 * The cookie value is base64url-encoded JSON, never raw JSON.
 *
 * Next.js (16.3) URL-decodes a Set-Cookie value written by the proxy a second
 * time when a page then reads cookies() (`mergeMiddlewareCookies` →
 * `parseSetCookie` → a bare `decodeURIComponent`). A raw-JSON value containing
 * a literal `%` therefore threw `URIError: URI malformed` and the page answered
 * 500 — for a campaign link as ordinary as `?utm_campaign=20%25%20off`, or any
 * landing with a `%` in its path or referrer (prod 2026-09-13: `/%C0`).
 * base64url has no `%` (nor any character a cookie or URL encoder touches), so
 * the double decode is harmless. UTF-8 bytes are encoded, so non-ASCII
 * campaign names survive.
 *
 * Cookies set before this change hold raw JSON; they are still read (a JSON
 * object starts with `{`, which base64url never does) until they expire.
 */

const toBase64Url = (text: string): string =>
  btoa(Array.from(new TextEncoder().encode(text), (byte) => String.fromCharCode(byte)).join(""))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const fromBase64Url = (encoded: string): string =>
  new TextDecoder("utf-8", { fatal: true }).decode(
    Uint8Array.from(atob(encoded.replace(/-/g, "+").replace(/_/g, "/")), (char) => char.charCodeAt(0)),
  );

/** Parse the sq_attr cookie value; null for missing/malformed/foreign shapes. */
export const parseAttributionCookie = (raw: string | undefined): AttributionState | null => {
  if (!raw) return null;
  try {
    const json = raw.startsWith("{") ? raw : fromBase64Url(raw);
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== "object" || parsed === null) return null;
    const state = parsed as Partial<AttributionState>;
    if (!isTouch(state.first) || !isTouch(state.last)) return null;
    return { first: state.first, last: state.last };
  } catch {
    return null;
  }
};

/**
 * Serialize for Set-Cookie, or null when the result would blow the header
 * budget (never break the site over attribution — with per-value caps this
 * only triggers on adversarial input).
 */
export const serializeAttributionCookie = (state: AttributionState): string | null => {
  const serialized = toBase64Url(JSON.stringify(state));
  return serialized.length > MAX_COOKIE_CHARS ? null : serialized;
};

/**
 * Human-facing acquisition source for a persisted touch row: explicit
 * utm_source, else the network implied by a click ID, else the referrer
 * host — null means genuinely direct/unknown.
 */
export const deriveAcquisitionSource = (row: {
  utm_source: string | null;
  click_ids: Record<string, string>;
  referrer: string | null;
}): string | null => {
  if (row.utm_source) return row.utm_source;
  const network = Object.keys(row.click_ids)
    .map((key) => CLICK_ID_NETWORKS[key])
    .find((slug) => slug !== undefined);
  if (network) return network;
  if (row.referrer) {
    try {
      return new URL(row.referrer).hostname;
    } catch {
      // An unparseable stored referrer (e.g. the pre-fix "null/" rows from
      // opaque-origin email-client referrers) is junk, not a source.
      return null;
    }
  }
  return null;
};
